"use client";

import { useState } from "react";
import Button, { ButtonSize, ButtonVariant } from "@/app/(components)/generics/Button/Button";
import IconInfo from "@/app/(components)/generics/Icons/IconInfo";
import { Input, TextInputType } from "@/app/(components)/generics/inputs/default/Input/Input";
import PhoneInput from "@/app/(components)/generics/inputs/default/PhoneInput/PhoneInput";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import SuccessMessage from "@/app/(components)/generics/messages/SuccessMessage/SuccessMessage";
import { Tooltip, TooltipPosition } from "@/app/(components)/generics/Tooltip/Tooltip";
import { updateAuthUser } from "@/app/actions/user/action";
import { sendResetPasswordRequest } from "@/app/actions/user/forgot-password/action";
import { UserStore } from "@/app/stores/UserStore";
import { TKey } from "@/app/translations/TKey";
import { T, TErrors } from "@/app/translations/Translations";
import styles from "./tabs.module.css";

type Props = { lang: string };

export default function Account({ lang }: Props) {
  const user = UserStore((state) => state.user);
  const setUser = UserStore((state) => state.setUser);

  const [data, setData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
    email: user?.email || "",
  });

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ firstName: "", lastName: "", phone: "" });

  if (!user) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setFieldErrors({ firstName: "", lastName: "", phone: "" });
    setSaving(true);
    const result = await updateAuthUser(data);
    if (result.success) {
      setUser({ ...user, ...data });
      setSuccess(true);
    } else if (result.fieldErrors) {
      setFieldErrors(TErrors(result.fieldErrors, lang));
    } else {
      setError(T(TKey.ERROR, lang) as string);
    }
    setSaving(false);
  };

  const handleResetPassword = async () => {
    setResetting(true);
    setError("");
    setResetSent(false);
    const ok = await sendResetPasswordRequest(user.email, lang);
    if (ok) {
      setResetSent(true);
    } else {
      setError(T(TKey.ERROR, lang) as string);
    }
    setResetting(false);
  };

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSave}>
        <div className={styles.row}>
          <Input
            data={data}
            dataKey="firstName"
            label={T(TKey.FIRST_NAME, lang) as string}
            lang={lang}
            required
            showRequiredLabel
            placeholder={T(TKey.FIRST_NAME, lang) as string}
            onChange={(d) => setData({ ...d })}
            errorMessage={fieldErrors.firstName}
          />
          <Input
            data={data}
            dataKey="lastName"
            label={T(TKey.LAST_NAME, lang) as string}
            lang={lang}
            required
            showRequiredLabel
            placeholder={T(TKey.LAST_NAME, lang) as string}
            onChange={(d) => setData({ ...d })}
            errorMessage={fieldErrors.lastName}
          />
        </div>
        <PhoneInput
          data={data}
          dataKey="phone"
          label={T(TKey.PHONE, lang) as string}
          lang={lang}
          showRequiredLabel
          placeholder={T(TKey.PHONE, lang) as string}
          onChange={(d) => setData({ ...d })}
          errorMessage={fieldErrors.phone}
        />
        <Input
          data={data}
          dataKey="email"
          type={TextInputType.EMAIL}
          label={T(TKey.EMAIL, lang) as string}
          labelSuffix={
            <Tooltip
              content={T(TKey.SETTINGS_EMAIL_READONLY_TOOLTIP, lang)}
              position={TooltipPosition.RIGHT}
            >
              <IconInfo />
            </Tooltip>
          }
          placeholder={T(TKey.EMAIL, lang) as string}
          onChange={() => {}}
          disabled={true}
        />

        {error && <ErrorMessage message={error} />}
        {success && (
          <SuccessMessage message={T(TKey.SAVE, lang) as string} onHide={() => setSuccess(false)} />
        )}

        <div className={styles.actions}>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            type="submit"
            loading={saving}
          >
            {T(TKey.SAVE, lang)}
          </Button>
        </div>
      </form>

      <div className={styles.separator} />

      <div className={styles.resetSection}>
        {error && <ErrorMessage message={error} />}
        {resetSent ? (
          <SuccessMessage message={T(TKey.SETTINGS_RESET_PASSWORD_SENT, lang) as string} />
        ) : (
          <Button
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SM}
            loading={resetting}
            onClick={handleResetPassword}
          >
            {T(TKey.SETTINGS_RESET_PASSWORD, lang)}
          </Button>
        )}
      </div>
    </div>
  );
}
